import type { HomePromptOption } from "@volley/data";

import type { Sport } from "../../common/context/sport";

/**
 * Sports the widget renders for. A set rather than a bare feature-flag check
 * because the `ASK_VOLLEY_HOME_PROMPT` feature cannot express "this sport's
 * prompt catalogue and content are ready" — that lands per sport, and this is
 * the switch for it. Typed against `Sport` so a typo is a compile error.
 *
 * Ships with pickleball alone, the only sport whose prompt catalogue is
 * authored today. Add a sport here once its prompts land; removing one again is
 * a one-line partial rollback that needs no flag change.
 */
export const ASK_VOLLEY_SPORTS: ReadonlySet<Sport> = new Set<Sport>([
    "PICKLEBALL",
]);

/** Option value that routes to the full workouts library instead of chat. */
export const BROWSE_ALL_VALUE = "browse_all";
/** Full workouts browse/search route (App → Trainer → AppWorkouts). */
export const WORKOUTS_SEARCH_ROUTE = "/content/apps/workouts/search";

/** Option value that starts the (all-skills) assessment from a no-assessment prompt. */
export const START_ASSESSMENT_VALUE = "start_assessment";
/** Option value that resumes an in-progress assessment from the "finish" prompt. */
export const FINISH_ASSESSMENT_VALUE = "finish_assessment";

/** Option value that opens the DUPR connect flow (the profile prompt) instead of chat. */
export const CONNECT_DUPR_VALUE = "connect_dupr";
/**
 * Option value that opens the APTA-id dialog — platform tennis's analogue of
 * connecting DUPR, since DUPR is a pickleball rating and PTI comes from an APTA
 * player id.
 */
export const CONNECT_APTA_VALUE = "connect_apta";

/** Option value that navigates to the performance dashboard instead of chat. */
export const SEE_STATS_VALUE = "see_my_stats";
/** The performance dashboard route ("see my stats" target). */
export const PERFORMANCE_ROUTE = "/performance";
/** Where the other sports' stats live: the assessments list. */
export const ASSESSMENTS_ROUTE = "/journeys/assessments";

/**
 * Where "See my stats" should land for a sport. `/performance` is the
 * pickleball-only dashboard, so every other sport goes to its assessments list
 * — the same split the home screen already makes for its own stats tiles.
 */
export function statsRouteForSport(sport: Sport): string {
    return sport === "PICKLEBALL" ? PERFORMANCE_ROUTE : ASSESSMENTS_ROUTE;
}

/**
 * True for the option that should bypass the chat and open the existing DUPR
 * connect flow (the Account DuprConnect SSO dialog, surfaced via useDuprConnect)
 * — the "I have a rating, let me link it" answer on the profile prompt. Mirrors
 * {@link isAssessmentLaunchOption}: a launch option, not a content route.
 */
export function isDuprConnectOption(option: HomePromptOption): boolean {
    return option.value === CONNECT_DUPR_VALUE;
}

/**
 * True for the platform-tennis counterpart of {@link isDuprConnectOption}: it
 * opens the existing APTA-id dialog so the user's PTI can be resolved. Reachable
 * only from a prompt authored with this option, so it stays inert until one
 * exists.
 */
export function isAptaConnectOption(option: HomePromptOption): boolean {
    return option.value === CONNECT_APTA_VALUE;
}

/**
 * True for the option that should bypass the chat and navigate to the
 * performance dashboard — the "See my stats" answer on the post-assessment
 * prompt. A navigate launch (like {@link isAssessmentLaunchOption}), not a
 * content route; its prompt is gated server-side on the user having a VSR result.
 */
export function isSeeStatsOption(option: HomePromptOption): boolean {
    return option.value === SEE_STATS_VALUE;
}

/**
 * True for the answer options that should bypass the chat entirely and drive the
 * assessment flow directly — starting the all-skills assessment (its start dialog)
 * or resuming one already in progress. Other assessment-routed options (e.g.
 * "see_full_rating", or the "practice_skills" recommendation chips) stay in the
 * normal chat → content-recommendation flow.
 */
export function isAssessmentLaunchOption(option: HomePromptOption): boolean {
    return (
        option.value === START_ASSESSMENT_VALUE ||
        option.value === FINISH_ASSESSMENT_VALUE
    );
}
